import {
  Achievement,
  GamificationData,
  UserStreak,
  LeaderboardEntry,
} from "../types/education";

// Achievement definitions
export const achievementDefinitions: Achievement[] = [
  // Lesson Completion Achievements
  {
    id: "first-lesson",
    title: "First Steps",
    description: "Complete your first lesson",
    icon: "🎯",
    category: "lessons",
    points: 10,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "common",
    requirements: {
      type: "lesson_completion",
      count: 1,
    },
  },
  {
    id: "lesson-streak-3",
    title: "On a Roll",
    description: "Complete 3 lessons in a row",
    icon: "🔥",
    category: "streaks",
    points: 25,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "common",
    requirements: {
      type: "lesson_streak",
      count: 3,
    },
  },
  {
    id: "lesson-streak-7",
    title: "Week Warrior",
    description: "Complete 7 lessons in a row",
    icon: "💪",
    category: "streaks",
    points: 50,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "rare",
    requirements: {
      type: "lesson_streak",
      count: 7,
    },
  },
  {
    id: "lesson-streak-30",
    title: "Monthly Master",
    description: "Complete 30 lessons in a row",
    icon: "👑",
    category: "streaks",
    points: 200,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "legendary",
    requirements: {
      type: "lesson_streak",
      count: 30,
    },
  },

  // Skill-Based Achievements
  {
    id: "color-basics-master",
    title: "Color Basics Master",
    description: "Complete all beginner color theory lessons",
    icon: "🎨",
    category: "skills",
    points: 75,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "rare",
    requirements: {
      type: "category_completion",
      category: "Foundations",
      difficulty: "beginner",
    },
  },
  {
    id: "harmony-expert",
    title: "Harmony Expert",
    description: "Complete all color harmony lessons",
    icon: "🌈",
    category: "skills",
    points: 100,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "epic",
    requirements: {
      type: "category_completion",
      category: "Harmony",
    },
  },
  {
    id: "accessibility-champion",
    title: "Accessibility Champion",
    description: "Complete all accessibility lessons",
    icon: "♿",
    category: "skills",
    points: 100,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "epic",
    requirements: {
      type: "category_completion",
      category: "Accessibility",
    },
  },

  // Quiz Achievements
  {
    id: "quiz-perfect",
    title: "Perfect Score",
    description: "Score 100% on any quiz",
    icon: "💯",
    category: "quizzes",
    points: 30,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "uncommon",
    requirements: {
      type: "quiz_score",
      score: 100,
    },
  },
  {
    id: "quiz-master",
    title: "Quiz Master",
    description: "Score 90% or higher on 5 different quizzes",
    icon: "🧠",
    category: "quizzes",
    points: 60,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "rare",
    requirements: {
      type: "quiz_excellence",
      count: 5,
      score: 90,
    },
  },

  // Time-Based Achievements
  {
    id: "speed-learner",
    title: "Speed Learner",
    description: "Complete a lesson in under 30 minutes",
    icon: "⚡",
    category: "time",
    points: 20,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "uncommon",
    requirements: {
      type: "lesson_time",
      maxTime: 1800, // 30 minutes in seconds
    },
  },
  {
    id: "dedicated-student",
    title: "Dedicated Student",
    description: "Spend 10 hours total learning",
    icon: "⏰",
    category: "time",
    points: 40,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "common",
    requirements: {
      type: "total_time",
      time: 36000, // 10 hours in seconds
    },
  },

  // Social Achievements
  {
    id: "helper",
    title: "Helpful Hand",
    description: "Help 5 other students by answering questions",
    icon: "🤝",
    category: "social",
    points: 35,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "uncommon",
    requirements: {
      type: "help_others",
      count: 5,
    },
  },
  {
    id: "community-leader",
    title: "Community Leader",
    description: "Reach the top 10 on any leaderboard",
    icon: "🏆",
    category: "social",
    points: 80,
    unlocked: false,
    unlockedAt: undefined,
    rarity: "epic",
    requirements: {
      type: "leaderboard_rank",
      rank: 10,
    },
  },
];

export class GamificationService {
  private storageKey = "chromis_gamification_data";

  constructor() {
    this.initializeData();
  }

  private initializeData(): void {
    const existing = this.getGamificationData();
    if (!existing) {
      const initialData: GamificationData = {
        totalPoints: 0,
        level: 1,
        experience: 0,
        achievements: [],
        currentStreak: {
          count: 0,
          startDate: new Date().toISOString(),
          lastActivityDate: new Date().toISOString(),
        },
        longestStreak: {
          count: 0,
          startDate: "",
          lastActivityDate: "",
        },
        weeklyStats: {
          lessonsCompleted: 0,
          timeSpent: 0,
          quizzesTaken: 0,
          averageScore: 0,
        },
        lastUpdated: new Date().toISOString(),
      };
      this.saveGamificationData(initialData);
    }
  }

  getGamificationData(): GamificationData | null {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error loading gamification data:", error);
      return null;
    }
  }

  private saveGamificationData(data: GamificationData): void {
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          ...data,
          lastUpdated: new Date().toISOString(),
        }),
      );
    } catch (error) {
      console.error("Error saving gamification data:", error);
    }
  }

  // Check and unlock achievements
  checkAchievements(progress: any, quizScores: any[]): Achievement[] {
    const data = this.getGamificationData();
    if (!data) return [];

    const newAchievements: Achievement[] = [];

    achievementDefinitions.forEach((achievement) => {
      if (data.achievements.find((a: Achievement) => a.id === achievement.id)) {
        return; // Already unlocked
      }

      if (
        this.checkAchievementRequirements(
          achievement,
          progress,
          quizScores,
          data,
        )
      ) {
        const unlockedAchievement = {
          ...achievement,
          unlocked: true,
          unlockedAt: new Date().toISOString(),
        };
        newAchievements.push(unlockedAchievement);
      }
    });

    if (newAchievements.length > 0) {
      const updatedData = {
        ...data,
        achievements: [...data.achievements, ...newAchievements],
        totalPoints:
          data.totalPoints +
          newAchievements.reduce((sum, a) => sum + a.points, 0),
      };
      this.saveGamificationData(updatedData);
    }

    return newAchievements;
  }

  private checkAchievementRequirements(
    achievement: Achievement,
    progress: any,
    quizScores: any[],
    data: GamificationData,
  ): boolean {
    const { requirements } = achievement;

    switch (requirements.type) {
      case "lesson_completion":
        return (
          this.countCompletedLessons(progress) >= (requirements.count || 0)
        );

      case "lesson_streak":
        return data.currentStreak.count >= (requirements.count || 0);

      case "category_completion":
        return this.checkCategoryCompletion(
          requirements.category || "",
          requirements.difficulty,
          progress,
        );

      case "quiz_score":
        return quizScores.some(
          (score) => score.score >= (requirements.score || 0),
        );

      case "quiz_excellence":
        const excellentScores = quizScores.filter(
          (score) => score.score >= (requirements.score || 0),
        );
        return excellentScores.length >= (requirements.count || 0);

      case "lesson_time":
        return this.checkFastLessonCompletion(
          progress,
          requirements.maxTime || 0,
        );

      case "total_time":
        return this.getTotalTimeSpent(progress) >= (requirements.time || 0);

      case "help_others":
        return this.getHelpCount() >= (requirements.count || 0);

      case "leaderboard_rank":
        return this.checkLeaderboardRank(requirements.rank || 0);

      default:
        return false;
    }
  }

  private countCompletedLessons(progress: any): number {
    return Object.values(progress).filter((p: any) => p.completed).length;
  }

  private checkCategoryCompletion(
    category: string,
    difficulty?: string,
    progress?: any,
  ): boolean {
    // This would need to be implemented based on lesson data
    // For now, return false as placeholder
    return false;
  }

  private checkFastLessonCompletion(progress: any, maxTime: number): boolean {
    // Check if any lesson was completed in less than maxTime seconds
    return Object.values(progress).some(
      (p: any) => p.completed && p.timeSpent && p.timeSpent < maxTime,
    );
  }

  private getTotalTimeSpent(progress?: any): number {
    if (!progress) return 0;
    return Object.values(progress).reduce(
      (total: number, p: any) => total + (p.timeSpent || 0),
      0,
    );
  }

  private getHelpCount(): number {
    // This would need to be implemented based on social features
    return 0;
  }

  private checkLeaderboardRank(rank: number): boolean {
    // This would need to be implemented based on leaderboard data
    return false;
  }

  // Update streak information
  updateStreak(lessonCompleted: boolean): void {
    const data = this.getGamificationData();
    if (!data) return;

    const today = new Date().toDateString();
    const lastActivity = new Date(
      data.currentStreak.lastActivityDate,
    ).toDateString();

    let newStreak = { ...data.currentStreak };

    if (lessonCompleted) {
      if (today === lastActivity) {
        // Same day, increment streak
        newStreak.count += 1;
      } else if (this.isYesterday(data.currentStreak.lastActivityDate)) {
        // Yesterday, continue streak
        newStreak.count += 1;
      } else {
        // Gap detected, reset streak
        newStreak = {
          count: 1,
          startDate: new Date().toISOString(),
          lastActivityDate: new Date().toISOString(),
        };
      }
      newStreak.lastActivityDate = new Date().toISOString();
    }

    // Update longest streak if needed
    const newLongestStreak =
      newStreak.count > data.longestStreak.count
        ? { ...newStreak, endDate: new Date().toISOString() }
        : data.longestStreak;

    const updatedData = {
      ...data,
      currentStreak: newStreak,
      longestStreak: newLongestStreak,
    };

    this.saveGamificationData(updatedData);
  }

  private isYesterday(dateString: string): boolean {
    const date = new Date(dateString);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date.toDateString() === yesterday.toDateString();
  }

  // Get leaderboard data
  getLeaderboard(
    type: "points" | "streaks" | "weekly" = "points",
  ): LeaderboardEntry[] {
    // This would typically fetch from a backend
    // For now, return mock data
    return [
      {
        rank: 1,
        userId: "user1",
        username: "ColorMaster",
        score: 2500,
        avatar: "🎨",
      },
      {
        rank: 2,
        userId: "user2",
        username: "HarmonyExpert",
        score: 2200,
        avatar: "🌈",
      },
      {
        rank: 3,
        userId: "user3",
        username: "QuickLearner",
        score: 2000,
        avatar: "⚡",
      },
      {
        rank: 4,
        userId: "user4",
        username: "StreakWarrior",
        score: 1800,
        avatar: "🔥",
      },
      {
        rank: 5,
        userId: "user5",
        username: "QuizChampion",
        score: 1600,
        avatar: "🧠",
      },
    ];
  }

  // Calculate level based on experience
  calculateLevel(experience: number): {
    level: number;
    currentLevelExp: number;
    nextLevelExp: number;
  } {
    const level = Math.floor(experience / 100) + 1;
    const currentLevelExp = (level - 1) * 100;
    const nextLevelExp = level * 100;

    return { level, currentLevelExp, nextLevelExp };
  }

  // Get achievement rarity color
  getRarityColor(rarity: string): string {
    switch (rarity) {
      case "common":
        return "#94a3b8";
      case "uncommon":
        return "#10b981";
      case "rare":
        return "#3b82f6";
      case "epic":
        return "#8b5cf6";
      case "legendary":
        return "#f59e0b";
      default:
        return "#94a3b8";
    }
  }
}
